import { useState, useEffect, useMemo } from "react";
import { useGame } from "./store";

export default function RecapModal() {
  const recap = useGame((s) => s.recap);
  const resolveRecap = useGame((s) => s.resolveRecap);

  // Slider value (the student's answer)
  const range = useMemo(() => {
    if (!recap) return { min: 0, max: 30, step: 0.5 };
    const corr = parseFloat(recap.correctAnswer);
    if (recap.unit === "m/s") {
      return { min: 0, max: Math.max(40, corr * 2), step: 0.5 };
    } else if (recap.unit === "m") {
      return { min: 0, max: Math.max(60, corr * 2), step: 1 };
    }
    return { min: 0, max: Math.max(30, corr * 2), step: 0.5 };
  }, [recap]);

  const [value, setValue] = useState(0);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    setValue(range.min);
    setFeedback(null);
  }, [recap, range.min]);

  if (!recap) return null;

  const correct = parseFloat(recap.correctAnswer);
  const tolerance = Math.abs(correct) * 0.12 + 0.5;
  const isCorrect = Math.abs(value - correct) <= tolerance;

  const onSubmit = () => {
    if (isCorrect) {
      setFeedback({ ok: true, message: "✓ Correct! Resuming without penalty." });
      setTimeout(() => resolveRecap(true), 1300);
    } else {
      setFeedback({
        ok: false,
        message: `Not quite. Correct ≈ ${recap.correctAnswer} ${recap.unit}. Losing 20 coins.`,
      });
      setTimeout(() => resolveRecap(false), 2300);
    }
  };

  return (
    <div
      data-testid="recap-modal"
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(2, 4, 12, 0.85)",
        backdropFilter: "blur(10px)",
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(620px, 100%)",
          background: "linear-gradient(160deg, #1a1d3a 0%, #0d0f1f 100%)",
          border: `1px solid ${isCorrect ? "#00ffaa" : "rgba(232,121,249,0.4)"}`,
          borderRadius: 22,
          padding: 30,
          color: "#fff",
          fontFamily: "'Inter', system-ui, sans-serif",
          boxShadow: isCorrect
            ? "0 24px 80px rgba(0,255,170,0.25)"
            : "0 24px 80px rgba(232,121,249,0.18)",
          transition: "all 0.3s",
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: 3,
            color: "#e879f9",
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          🎓 PHYSICS RECAP
        </div>
        <h2 style={{ margin: 0, fontSize: 24, lineHeight: 1.3 }}>
          What went wrong?
        </h2>
        <p style={{ color: "#cfd6e3", fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          {recap.reason}
        </p>

        <div
          style={{
            marginTop: 16,
            padding: "12px 14px",
            background: "rgba(255,255,255,0.04)",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.08)",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 13,
          }}
        >
          <div style={{ color: "#9aa4b2", fontSize: 11, marginBottom: 6 }}>
            EQUATION
          </div>
          <div style={{ color: "#ffd23f", fontSize: 16 }}>{recap.equation}</div>
          <div style={{ color: "#9aa4b2", fontSize: 11, marginTop: 10, marginBottom: 4 }}>
            GIVEN
          </div>
          <div style={{ color: "#3eb6ff" }}>{recap.given}</div>
        </div>

        <div style={{ marginTop: 22 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 4,
            }}
          >
            <label
              style={{
                fontSize: 12,
                color: "#9aa4b2",
                letterSpacing: 1,
              }}
            >
              <b style={{ color: "#fff" }}>{recap.parameter}</b>
            </label>
            <span
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: isCorrect ? "#00ffaa" : "#ffd23f",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {value.toFixed(1)} {recap.unit}
            </span>
          </div>
          <input
            data-testid="recap-slider"
            type="range"
            min={range.min}
            max={range.max}
            step={range.step}
            value={value}
            onChange={(e) => setValue(parseFloat(e.target.value))}
            style={{
              width: "100%",
              accentColor: isCorrect ? "#00ffaa" : "#ffd23f",
            }}
          />
          <div
            style={{
              marginTop: 8,
              padding: "8px 12px",
              borderRadius: 10,
              background: isCorrect
                ? "rgba(0,255,170,0.14)"
                : "rgba(255,77,109,0.10)",
              color: isCorrect ? "#00ffaa" : "#ff8a9b",
              fontSize: 13,
              fontWeight: 600,
              border: isCorrect
                ? "1px solid rgba(0,255,170,0.5)"
                : "1px solid rgba(255,77,109,0.25)",
              boxShadow: isCorrect ? "0 0 24px rgba(0,255,170,0.3)" : "none",
              transition: "all 0.25s",
            }}
          >
            {isCorrect
              ? "✓ This value satisfies the equation — submit!"
              : `Difference from correct: ${Math.abs(value - correct).toFixed(1)} ${recap.unit}`}
          </div>
        </div>

        <button
          data-testid="recap-submit-btn"
          onClick={onSubmit}
          disabled={!!feedback}
          style={{
            marginTop: 18,
            width: "100%",
            padding: "14px 22px",
            background: isCorrect
              ? "linear-gradient(135deg, #00ffaa 0%, #00b8a9 100%)"
              : "linear-gradient(135deg, #e879f9 0%, #ff4d6d 100%)",
            border: "none",
            borderRadius: 12,
            color: isCorrect ? "#001a14" : "#fff",
            fontSize: 16,
            fontWeight: 800,
            cursor: feedback ? "default" : "pointer",
            opacity: feedback ? 0.7 : 1,
            letterSpacing: 1,
          }}
        >
          Submit answer
        </button>

        {feedback && (
          <div
            data-testid="recap-feedback"
            style={{
              marginTop: 14,
              padding: "10px 14px",
              borderRadius: 10,
              background: feedback.ok
                ? "rgba(0, 255, 170, 0.12)"
                : "rgba(255, 77, 109, 0.12)",
              color: feedback.ok ? "#00ffaa" : "#ff4d6d",
              fontSize: 14,
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            {feedback.message}
          </div>
        )}
      </div>
    </div>
  );
}
